/* eslint-disable react-refresh/only-export-components */
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Tenant-timezone datetime display. Every logged event (notes, calls,
 * meetings, emails) shows its full date AND time in the workspace timezone
 * (default Eastern, configurable in Settings), with the zone abbreviation
 * so there's never ambiguity across offices.
 */

export const DEFAULT_TIMEZONE = "America/New_York";

export const TIMEZONE_CHOICES = [
  { id: "America/New_York", name: "Eastern (US)" },
  { id: "America/Chicago", name: "Central (US)" },
  { id: "America/Denver", name: "Mountain (US)" },
  { id: "America/Phoenix", name: "Arizona" },
  { id: "America/Los_Angeles", name: "Pacific (US)" },
  { id: "America/Anchorage", name: "Alaska" },
  { id: "Pacific/Honolulu", name: "Hawaii" },
  { id: "Etc/UTC", name: "UTC / GMT" },
  { id: "Europe/London", name: "London (GMT+0/+1)" },
  { id: "Europe/Paris", name: "Central Europe (GMT+1/+2)" },
  { id: "Asia/Dubai", name: "Dubai (GMT+4)" },
  { id: "Asia/Kolkata", name: "India (GMT+5:30)" },
  { id: "Asia/Singapore", name: "Singapore (GMT+8)" },
  { id: "Australia/Sydney", name: "Sydney (GMT+10/+11)" },
];

/** Current GMT offset label for a zone, e.g. "GMT-4". */
export const gmtOffsetLabel = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};

export const useTimezone = (): string => {
  const { timezone } = useConfigurationContext();
  return timezone || DEFAULT_TIMEZONE;
};

export const formatDateTime = (iso: string, tz: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString("en-US", {
    timeZone: tz,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

/** Absolute date + time in the workspace timezone. */
export const DateTimeStamp = ({
  date,
  className,
}: {
  date: string;
  className?: string;
}) => {
  const tz = useTimezone();
  return (
    <span className={className} title={new Date(date).toISOString()}>
      {formatDateTime(date, tz)}
    </span>
  );
};
